import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import {
  STATUS_ICONS,
  STATUS_OPTIONS,
  MANUAL_STATUS_OPTIONS,
  STATUS_COLORS,
  BACKGROUND_STATUS_COLORS,
  STATUS_PRICE_UPDATED,
  formatOodEditedBy,
} from '../../../utility/statusConfig';
import axios from 'axios';
import envConfig from '../../../bin/env.config';
import { useSelector } from 'react-redux';
import StoreCheckHistoryTable from './StoreCheckHistoryTable';

export default function DialogCheckAndUpdateStoreStatus({ open, onClose, statusData, setStatusData }) {
  const [formData, setFormData] = useState(statusData);
  const token = useSelector((state) => state.session.token);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData(statusData);
    }
  }, [open, statusData]);

  const isReadOnly = Number(formData.status_id) === STATUS_PRICE_UPDATED;

  const handleChange = (e) => {
    if (isReadOnly) return;
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (isReadOnly) return;
    try {
      setLoading(true);
      await axios.post(`${envConfig.API_ROOT}${envConfig.API_VERSION}/company-report-tool/data-freshness-reporting/out-of-date-store-check-info-update`, formData, {
        headers: {
          Authorization: token,
        },
      });
      onClose();
    } catch (error) {
      console.error('Error saving store check data:', error);
    } finally {
      setStatusData(formData);
      setLoading(false);
    }
  };

  const statusLabel = STATUS_OPTIONS[formData.status_id] || '';
  const formattedEditedAt = formData.edited_at ? new Date(formData.edited_at).toLocaleString() : '';
  const editedByLabel = formatOodEditedBy(formData.edited_by, {
    statusId: formData.status_id,
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Store Product Status Information</DialogTitle>
      <DialogContent>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, color: STATUS_COLORS[formData.status_id], backgroundColor: BACKGROUND_STATUS_COLORS[formData.status_id] }}>
            <span>{statusLabel}</span>
            {STATUS_ICONS[formData.status_id]}
            {formattedEditedAt && (
              <div style={{ color: STATUS_COLORS[formData.status_id], fontSize: 13, gap: 16, marginLeft: 16 }}>
                Last Updated: {formattedEditedAt}
                {editedByLabel ? ` · ${editedByLabel}` : ''}
              </div>
            )}
          </div>

          <TextField
            select
            label="Status"
            name="status_id"
            value={isReadOnly ? STATUS_PRICE_UPDATED : (formData.status_id ?? 0)}
            onChange={handleChange}
            fullWidth
            disabled={isReadOnly}
            style={{ color: STATUS_COLORS[formData.status_id], backgroundColor: BACKGROUND_STATUS_COLORS[formData.status_id] }}
          >
            {isReadOnly ? (
              <MenuItem value={STATUS_PRICE_UPDATED} style={{ color: STATUS_COLORS[5], backgroundColor: BACKGROUND_STATUS_COLORS[5] }}>
                {STATUS_OPTIONS[5]}
              </MenuItem>
            ) : (
              Object.keys(MANUAL_STATUS_OPTIONS).map((key) => (
                <MenuItem key={key} value={key} style={{ color: STATUS_COLORS[key], backgroundColor: BACKGROUND_STATUS_COLORS[key] }}>
                  {MANUAL_STATUS_OPTIONS[key]}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            label="Price"
            name="price"
            type="number"
            value={formData.price === undefined || formData.price === null ? '' : formData.price}
            onChange={handleChange}
            fullWidth
            disabled={isReadOnly}
          />

          <TextField
            label="Note"
            name="note"
            value={formData.note}
            onChange={handleChange}
            multiline
            minRows={2}
            fullWidth
            disabled={isReadOnly}
          />
        </div>
        <div style={{ marginTop: '24px' }}>
          <h3>Check History</h3>
          <StoreCheckHistoryTable
            company_product_id={formData.company_product_id}
            store_id={formData.store_id}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" disabled={loading}>
          {isReadOnly ? 'Close' : 'Cancel'}
        </Button>
        {!isReadOnly && (
          <Button onClick={handleSave} color="primary" variant="contained" disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
